import { useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
    searchClientDocuments,
    updateDocumentStatus,
    bulkUpdateDocumentStatus,
    bulkDocumentsZip,
    createCheckRegisterUploadUrl,
    rejectCheckRegister,
} from "../../../utils/apiUtils";
import { useAuthStore } from "../../../store/authStore";

const DEFAULT_FILTERS = {
    status: "in-review",
    accounts: [],
    dateFrom: null,
    dateTo: null,
};

const DEFAULT_PAGE_SIZE = 20;

const useCheckRegisterReview = () => {
    const { email: reviewer } = useAuthStore();

    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [tokenStack, setTokenStack] = useState([]);
    const [currentToken, setCurrentToken] = useState(null);

    const accountHccIds = appliedFilters.accounts?.length
        ? appliedFilters.accounts.map((o) => o.value)
        : undefined;

    const queryFilters = {
        status: appliedFilters.status ?? undefined,
        accountHccIds,
        dateFrom: appliedFilters.dateFrom ?? undefined,
        dateTo: appliedFilters.dateTo ?? undefined,
        limit: pageSize,
        nextToken: currentToken ?? undefined,
    };

    const queryKey = ["checkRegisterDocuments", queryFilters];

    const { data, isLoading, isError, isFetching, refetch } = useQuery({
        queryKey,
        queryFn: () => searchClientDocuments({
            docType: "check-register",
            ...(appliedFilters.status && { status: appliedFilters.status }),
            ...(appliedFilters.dateFrom && { dateFrom: appliedFilters.dateFrom }),
            ...(appliedFilters.dateTo && { dateTo: appliedFilters.dateTo }),
            ...(accountHccIds && { accountHccIds }),
            limit: pageSize,
            ...(currentToken && { nextToken: currentToken }),
        }),
        select: (raw) => ({
            items: raw?.documents ?? [],
            nextToken: raw?.nextToken ?? null,
        }),
    });

    const items = data?.items ?? [];
    const nextToken = data?.nextToken ?? null;

    const applyFilters = useCallback((newFilters) => {
        setAppliedFilters(newFilters);
        setTokenStack([]);
        setCurrentToken(null);
    }, []);

    const clearFilters = useCallback(() => {
        setFilters(DEFAULT_FILTERS);
        applyFilters(DEFAULT_FILTERS);
    }, [applyFilters]);

    const goNext = useCallback(() => {
        if (!nextToken) return;
        setTokenStack((prev) => [...prev, currentToken]);
        setCurrentToken(nextToken);
    }, [nextToken, currentToken]);

    const goPrevious = useCallback(() => {
        if (tokenStack.length === 0) return;
        const stack = [...tokenStack];
        const prev = stack.pop();
        setTokenStack(stack);
        setCurrentToken(prev);
    }, [tokenStack]);

    const onPageSizeChange = useCallback((newSize) => {
        setPageSize(newSize);
        setTokenStack([]);
        setCurrentToken(null);
    }, []);

    const [selectedIds, setSelectedIds] = useState(new Set());

    const toggleRowSelection = (sk) =>
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(sk) ? next.delete(sk) : next.add(sk);
            return next;
        });

    const toggleAllSelection = () => {
        const selectable = items.filter((i) => i.status === 'in-review');
        setSelectedIds((prev) =>
            prev.size === selectable.length ? new Set() : new Set(selectable.map((i) => i.sk))
        );
    };

    const clearSelection = () => setSelectedIds(new Set());
    const canBulkAccept = selectedIds.size > 0;

    const bulkActionMutation = useMutation({
        mutationFn: async () => {
            const selectedItems = items
                .filter((i) => selectedIds.has(i.sk))
                .map((i) => ({ accountHccId: i.pk, sk: i.sk, status: 'accepted' }));
            return bulkUpdateDocumentStatus({ reviewer, items: selectedItems });
        },
        onSuccess: (data) => {
            const successes = data.items.filter((i) => i.success).length;
            data.items.filter((i) => !i.success).forEach((f) => toast.error(`${f.sk}: ${f.error}`));
            toast.success(`Approved ${successes} check-register(s)`);
            clearSelection();
            refetch();
        },
        onError: (err) => toast.error(err.message ?? 'Bulk approve failed'),
    });

    const bulkDownloadMutation = useMutation({
        mutationFn: async () => {
            const selectedItems = items
                .filter((i) => selectedIds.has(i.sk))
                .map((i) => ({ pk: i.pk, sk: i.sk }));
            return bulkDocumentsZip(selectedItems);
        },
        onSuccess: (data) => {
            toast.success(`Downloading ZIP with ${data.totalFiles} files`);
            window.location.href = data.downloadUrl;
        },
        onError: (err) => toast.error(err.message ?? 'Download failed'),
    });

    const acceptDoc = useMutation({
        mutationFn: ({ sk, accountHccId }) =>
            updateDocumentStatus({ sk, accountHccId, status: "accepted", reviewer }),
        onSuccess: () => {
            window.location.reload();
        },
        onError: () => {
            toast.error("Service unavailable.");
        },
    });

    const createUploadUrl = useMutation({
        mutationFn: ({ accountHccId }) => createCheckRegisterUploadUrl({ accountHccId }),
    });

    const rejectWithFiles = useMutation({
        mutationFn: ({ sk, accountHccId, phiStagingKey, noPhiStagingKey }) =>
            rejectCheckRegister({ sk, accountHccId, reviewer, phiStagingKey, noPhiStagingKey }),
        onSuccess: () => {
            window.location.reload();
        },
        onError: () => {
            toast.error("Service unavailable.");
        },
    });

    return {
        items,
        nextToken,
        isLoading,
        isError,
        isFetching,
        refetch,
        filters,
        setFilters,
        appliedFilters,
        applyFilters,
        clearFilters,
        pageSize,
        onPageSizeChange,
        tokenStack,
        goNext,
        goPrevious,
        selectedIds,
        toggleRowSelection,
        toggleAllSelection,
        clearSelection,
        canBulkAccept,
        bulkActionMutation,
        bulkDownloadMutation,
        mutations: { acceptDoc, createUploadUrl, rejectWithFiles },
    };
};

export default useCheckRegisterReview;
